/**
 * PlatformService.js
 * 
 * High-fidelity abstraction layer for clinical tasks across multiple platforms
 * Supported: Web, Electron (Desktop), Capacitor (Android/iOS)
 */

export const PlatformType = {
  WEB: 'web',
  ELECTRON: 'electron',
  CAPACITOR: 'capacitor',
};

class PlatformService {
  constructor() {
    this.platform = this.detectPlatform();
  }

  detectPlatform() {
    if (window.electron && window.electron.isElectron) {
      return PlatformType.ELECTRON;
    }
    if (window.Capacitor && window.Capacitor.getPlatform() !== 'web') {
      return PlatformType.CAPACITOR;
    }
    return PlatformType.WEB;
  }

  isElectron() {
    return this.platform === PlatformType.ELECTRON;
  }

  isCapacitor() {
    return this.platform === PlatformType.CAPACITOR;
  }

  isMobile() {
    return this.isCapacitor();
  }

  /**
   * Triggers a professional clinical print action
   * @param {string} htmlContent - The medical-grade HTML report
   * @param {string} patientName - Context for the file name/header
   */
  async print(htmlContent, patientName = 'Patient_Report') {
    if (this.isElectron() || this.platform === PlatformType.WEB) {
      // High-performance window print for clinical desktops
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        // Wait for high-fidelity resources to load if any
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        alert('Please allow pop-ups to print the medical history report.');
      }
    } else if (this.isCapacitor()) {
      // High-fidelity mobile printing via Capacitor plugin (placeholder for future implementation)
      console.log('Mobile printing triggered for:', patientName);
      // NOTE: We will integrate @capacitor-community/print here in the next step
      alert('Mobile printing is being initialized. Please use the Desktop version for immediate physical printing.');
    }
  }

  /**
   * High-fidelity clinical file management
   */
  async saveFile(blob, fileName) {
    if (this.isElectron()) {
      // Electron-specific save logic (via IPC)
      console.log('Saving to clinical desktop:', fileName);
    } else {
      // Standard web/capacitor save
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }
}

export default new PlatformService();
